/**
 * textoImagem.ts — Categoria "Texto e imagem" (10 modelos premium)
 *
 * Cada modelo é uma composição editável: seção com camadas + primitivos.
 * Fotos vêm do acervo compartilhado (`acervo://`), nunca de endereço solto.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote A)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { AVATARES, FOTO, WEB, b } from './_shared';

const modelos: StudioPatternDefinition[] = [
  {
    key: 'texto-imagem.foto-direita',
    category: 'texto-imagem',
    label: 'Texto com foto à direita',
    description: 'Conteúdo à esquerda, foto arredondada com sombra à direita.',
    preview: 'split-right',
    outputs: WEB,
    order: 1,
    build: () => [
      b(
        'section.layered',
        { layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', motion: 'fade-up' },
        [
          b('ui.badge', { text: 'Sobre nós', variant: 'soft' }),
          b('ui.title', { text: 'Tecnologia simples para um problema real', highlight: 'problema real', size: 'lg', level: 'h2' }),
          b('ui.paragraph', {
            text: 'Unimos processo, dados e atendimento próximo para que a sua operação cresça sem virar bagunça.',
            size: 'md',
            maxWidth: '520px',
          }),
          b('ui.buttons', { items: 'Conhecer a solução|#recursos|solid', size: 'md' }),
          b('ui.image', { src: FOTO.reuniao, alt: 'Equipe em reunião', ratio: 'landscape', radius: 'xl', shadow: 'lg', motion: 'scale' }),
        ],
      ),
    ],
  },

  {
    key: 'texto-imagem.foto-esquerda',
    category: 'texto-imagem',
    label: 'Foto à esquerda com selo',
    description: 'Foto panorâmica à esquerda e texto respirado à direita.',
    preview: 'split-left',
    outputs: WEB,
    order: 2,
    build: () => [
      b(
        'section.layered',
        { layout: 'split-reverse', paddingY: 'lg', align: 'left', maxWidth: '1120px', backgroundColor: '#f8fafc', motion: 'fade' },
        [
          b('ui.image', { src: FOTO.escritorio, alt: 'Escritório da empresa', ratio: 'landscape', radius: 'xl', shadow: 'md' }),
          b('ui.badge', { text: 'Nossa forma de trabalhar', variant: 'outline' }),
          b('ui.title', { text: 'Perto do cliente, do começo ao resultado', size: 'lg', level: 'h2' }),
          b('ui.paragraph', {
            text: 'Um time responsável por cada conta, reuniões objetivas e um painel aberto com o andamento de tudo.',
            maxWidth: '520px',
          }),
        ],
      ),
    ],
  },

  {
    key: 'texto-imagem.destaque-escuro',
    category: 'texto-imagem',
    label: 'Bloco escuro com foto',
    description: 'Fundo escuro com brilho, foto em moldura e chamada forte.',
    preview: 'split-right',
    outputs: WEB,
    order: 3,
    build: () => [
      b(
        'section.layered',
        {
          layout: 'split',
          paddingY: 'xl',
          align: 'left',
          backgroundGradient: 'mesh-dark',
          glow: 'top-right',
          textColor: '#ffffff',
          radius: 'xl',
          motion: 'fade-up',
        },
        [
          b('ui.badge', { text: 'Plataforma', variant: 'glass' }),
          b('ui.title', { text: 'Tudo em um lugar só, com a sua cara', highlight: 'sua cara', size: 'xl', level: 'h2', accentColor: '#7dd3fc' }),
          b('ui.paragraph', { text: 'Cadastros, propostas, cobranças e atendimento conversando entre si, sem retrabalho.', size: 'lg', maxWidth: '520px' }),
          b('ui.device', { src: FOTO.painel, alt: 'Tela do sistema', device: 'browser', shadow: 'glow', motion: 'floating' }),
        ],
      ),
    ],
  },

  {
    key: 'texto-imagem.cartao-flutuante',
    category: 'texto-imagem',
    label: 'Foto com cartão flutuante',
    description: 'Imagem grande com um cartão de destaque por cima do conteúdo.',
    preview: 'split-left',
    outputs: WEB,
    order: 4,
    build: () => [
      b(
        'section.layered',
        { layout: 'split-reverse', paddingY: 'lg', align: 'left', maxWidth: '1120px', pattern: 'dots', motion: 'fade-up' },
        [
          b('ui.image', { src: FOTO.consultoria, alt: 'Consultoria com cliente', ratio: 'portrait', radius: 'xl', shadow: 'lg' }),
          b('ui.title', { text: 'Consultoria que entra no detalhe', size: 'lg', level: 'h2' }),
          b('ui.paragraph', { text: 'Diagnóstico, plano de ação e acompanhamento semanal com indicadores claros.' }),
          b('ui.card', {
            icon: '★',
            title: 'Nota 4,9 de 5',
            text: 'Avaliação média dos clientes nos últimos 12 meses.',
            variant: 'glass',
            radius: 'xl',
            shadow: 'lg',
            motion: 'floating',
          }),
        ],
      ),
    ],
  },

  {
    key: 'texto-imagem.numeros-ao-lado',
    category: 'texto-imagem',
    label: 'Texto, foto e números',
    description: 'Conteúdo com dois números de resultado ao lado da foto.',
    preview: 'split-right',
    outputs: WEB,
    order: 5,
    build: () => [
      b(
        'section.layered',
        { layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', motion: 'fade' },
        [
          b('ui.title', { text: 'Resultado que aparece no caixa', highlight: 'no caixa', size: 'lg', level: 'h2' }),
          b('ui.paragraph', { text: 'Menos tempo em tarefa repetida, mais tempo vendendo e atendendo bem.' }),
          b('ui.stat', { value: '+38%', label: 'de propostas aceitas' }),
          b('ui.stat', { value: '-52%', label: 'de tempo em tarefas manuais' }),
          b('ui.image', { src: FOTO.dados, alt: 'Painel com indicadores', ratio: 'landscape', radius: 'xl', shadow: 'md', motion: 'scale' }),
        ],
      ),
    ],
  },

  {
    key: 'texto-imagem.mosaico',
    category: 'texto-imagem',
    label: 'Mosaico com duas fotos',
    description: 'Texto e duas fotos em mosaico, com respiro generoso.',
    preview: 'grid-2',
    outputs: WEB,
    order: 6,
    build: () => [
      b(
        'section.layered',
        { layout: 'bento', paddingY: 'lg', align: 'left', maxWidth: '1120px', backgroundColor: '#f8fafc', motion: 'fade-up' },
        [
          b('ui.title', { text: 'Um jeito de trabalhar que combina com a sua empresa', size: 'md', level: 'h2' }),
          b('ui.image', { src: FOTO.criativo, alt: 'Time criativo', ratio: 'square', radius: 'xl', shadow: 'md' }),
          b('ui.image', { src: FOTO.mesaTrabalho, alt: 'Mesa de trabalho', ratio: 'square', radius: 'xl', shadow: 'md' }),
          b('ui.paragraph', { text: 'Do primeiro contato à entrega, cada etapa tem responsável, prazo e acompanhamento.' }),
        ],
      ),
    ],
  },

  {
    key: 'texto-imagem.prova-social',
    category: 'texto-imagem',
    label: 'Texto com prova social',
    description: 'Foto ao lado do texto, com fotos de clientes e nota média.',
    preview: 'split-left',
    outputs: WEB,
    order: 7,
    build: () => [
      b(
        'section.layered',
        { layout: 'split-reverse', paddingY: 'lg', align: 'left', maxWidth: '1120px', motion: 'fade' },
        [
          b('ui.image', { src: FOTO.atendimento, alt: 'Atendimento ao cliente', ratio: 'landscape', radius: 'xl', shadow: 'lg' }),
          b('ui.title', { text: 'Quem começa, continua com a gente', size: 'lg', level: 'h2' }),
          b('ui.paragraph', { text: 'Atendimento humano, resposta rápida e um time que conhece o seu negócio pelo nome.' }),
          b('ui.avatars', { items: AVATARES, text: '+2.000 clientes atendidos', note: 'Nota 4,9 de 5 em avaliações' }),
        ],
      ),
    ],
  },

  {
    key: 'texto-imagem.faixa-larga',
    category: 'texto-imagem',
    label: 'Faixa com foto de fundo',
    description: 'Foto ocupa a seção inteira e o texto fica sobre ela.',
    preview: 'hero-image',
    outputs: WEB,
    order: 8,
    build: () => [
      b(
        'section.layered',
        {
          backgroundImage: FOTO.arquitetura,
          overlay: 'left',
          paddingY: 'xl',
          minHeight: 'md',
          align: 'left',
          textColor: '#ffffff',
          maxWidth: '1120px',
          motion: 'fade',
        },
        [
          b('ui.badge', { text: 'Estrutura própria', variant: 'glass' }),
          b('ui.title', { text: 'Presença onde o seu cliente está', size: 'xl', level: 'h2' }),
          b('ui.paragraph', { text: 'Unidades preparadas, equipe treinada e processo padronizado em cada praça.', maxWidth: '520px' }),
          b('ui.buttons', { items: 'Ver unidades|#contato|glass' }),
        ],
      ),
    ],
  },

  {
    key: 'texto-imagem.produto-detalhe',
    category: 'texto-imagem',
    label: 'Detalhe do produto',
    description: 'Foto do produto em destaque e três benefícios em cartões.',
    preview: 'split-right',
    outputs: WEB,
    order: 9,
    build: () => [
      b(
        'section.layered',
        { layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', backgroundGradient: 'soft', motion: 'fade-up' },
        [
          b('ui.title', { text: 'Feito para durar e fácil de usar', size: 'lg', level: 'h2' }),
          b('ui.card', { icon: '✓', title: 'Instalação em minutos', text: 'Sem obra e sem parar a operação.', variant: 'solid', radius: 'lg' }),
          b('ui.card', { icon: '✓', title: 'Garantia estendida', text: 'Suporte técnico incluso por 24 meses.', variant: 'solid', radius: 'lg' }),
          b('ui.image', { src: FOTO.produto, alt: 'Produto em destaque', ratio: 'square', radius: 'xl', shadow: 'lg', motion: 'floating' }),
        ],
      ),
    ],
  },

  {
    key: 'texto-imagem.coluna-larga',
    category: 'texto-imagem',
    label: 'Coluna larga com foto pequena',
    description: 'Texto ocupa a maior parte e a foto entra como apoio.',
    preview: 'split-right',
    outputs: WEB,
    order: 10,
    build: () => [
      b(
        'section.layered',
        { layout: 'split-wide-left', paddingY: 'lg', align: 'left', maxWidth: '1120px', pattern: 'grid', motion: 'fade' },
        [
          b('ui.badge', { text: 'História', variant: 'soft' }),
          b('ui.title', { text: 'Dez anos resolvendo o que ninguém queria encarar', size: 'lg', level: 'h2' }),
          b('ui.paragraph', {
            text: 'Começamos atendendo três clientes na cidade. Hoje somos referência no setor, com o mesmo cuidado do primeiro dia.',
            maxWidth: '640px',
          }),
          b('ui.image', { src: FOTO.interior, alt: 'Ambiente da empresa', ratio: 'portrait', radius: 'xl', shadow: 'md' }),
        ],
      ),
    ],
  },
];

registerPatterns(modelos);
